import {
  HttpAgent,
  type AgentSubscriber,
  type Context,
  type RunAgentInput,
} from "@ag-ui/client";
import { getConfig } from "./config";
import { csrfToken } from "@/auth/csrf";

// AG-UI transport for the Assistant chat turn (agent-service ADR 0020). Replaces the hand-rolled
// streamAgent/SSE parser with the official @ag-ui/client HttpAgent, pointed at the BFF /ai/agui
// passthrough (which injects the Bearer, exactly like /ai/stream). Sessions stay
// server-authoritative: the agent-service reads only the LAST user message of `messages[]`, so we
// set exactly that one message per run instead of accumulating client history. The ADR-0014
// person-load endpoints still use the legacy parser in agent-stream.ts.

// Cookie-session auth + per-request CSRF header (read at request time — the token can rotate),
// mirroring what streamAgent's fetch options did.
class SparkAgent extends HttpAgent {
  protected override requestInit(input: RunAgentInput): RequestInit {
    const init = super.requestInit(input);
    const csrf = csrfToken();
    return {
      ...init,
      credentials: "include",
      headers: {
        ...(init.headers as Record<string, string>),
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
    };
  }
}

export function createAssistantAgent(): HttpAgent {
  return new SparkAgent({ url: `${getConfig().basePath}ai/agui` });
}

// The drawer's view of a turn — same shape the old streamAgent callbacks fed, so the store wiring
// is unchanged. `onToolCall` fires at TOOL_CALL_END with the fully-parsed args.
export interface AguiTurnHandlers {
  onThreadId: (id: string) => void;
  onDelta: (delta: string) => void;
  onToolCall: (name: string, args: Record<string, unknown>) => void;
  onSuggestions: (items: string[]) => void;
  onError: () => void;
}

export interface AgentBindingContext {
  matchId: string;
}

// Run one chat turn. Resolves when the run finishes (RUN_FINISHED / RUN_ERROR / abort); rejects on
// transport failure before/while streaming. `threadId` is the agent-service session id (null →
// let the client mint one; the server creates the session under it and echoes it in RUN_STARTED).
export async function runAguiTurn(
  agent: HttpAgent,
  opts: {
    message: string;
    section: string;
    threadId: string | null;
    uiContext: Context[];
    bindingContext?: AgentBindingContext;
    abortController: AbortController;
  },
  handlers: AguiTurnHandlers,
): Promise<void> {
  if (opts.threadId) agent.threadId = opts.threadId;
  // Server-authoritative history: only the latest user message rides up (see module comment).
  agent.setMessages([{ id: crypto.randomUUID(), role: "user", content: opts.message }]);

  const subscriber: AgentSubscriber = {
    onRunStartedEvent: ({ event }) => handlers.onThreadId(event.threadId),
    onTextMessageContentEvent: ({ event }) => handlers.onDelta(event.delta),
    onToolCallEndEvent: ({ toolCallName, toolCallArgs }) =>
      handlers.onToolCall(toolCallName, toolCallArgs),
    onCustomEvent: ({ event }) => {
      if (event.name === "suggestions" && Array.isArray(event.value)) {
        handlers.onSuggestions(event.value.filter((v): v is string => typeof v === "string"));
      }
    },
    onRunErrorEvent: () => handlers.onError(),
  };

  await agent.runAgent(
    {
      context: opts.uiContext,
      forwardedProps: {
        section: opts.section,
        ...(opts.bindingContext ? { context: opts.bindingContext } : {}),
      },
      abortController: opts.abortController,
    },
    subscriber,
  );
}
